/**
 * The single HTTP entry point of the application.
 *
 * Components and features never call `fetch`/`axios` directly (ESLint enforces
 * it) -- they call a function in `src/api/*.api.js`, which calls the
 * helpers exported here. This is where base URL, auth headers, token refresh
 * and error normalisation live.
 */

import axios from 'axios';

import { config } from '@/app/config';
import { tokenStorage } from '@/services/tokenStorage';
import { ApiError } from '@/types/api';

const REQUEST_ID_HEADER = 'X-Request-ID';

/** Axios instance used by every API module. */
export const apiClient = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 30_000,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  // FastAPI binds repeated keys (?work_modes=REMOTE&work_modes=HYBRID); axios
  // would otherwise emit work_modes[]=… which never reaches the endpoint.
  paramsSerializer: { indexes: null },
});

/** Bare instance for the refresh call, so it never re-enters the interceptors. */
const refreshClient = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

let onSessionExpired = () => {};

/** Registered by AuthProvider so an expired session can redirect to login. */
export function setSessionExpiredHandler(handler) {
  onSessionExpired = handler;
}

// ---------------------------------------------------------------------------
// Request: attach the access token
// ---------------------------------------------------------------------------
apiClient.interceptors.request.use((request) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    request.headers.set('Authorization', `Bearer ${token}`);
  }
  return request;
});

// ---------------------------------------------------------------------------
// Response: refresh once on 401, then normalise errors
// ---------------------------------------------------------------------------
let refreshInFlight = null;

async function refreshAccessToken() {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) return null;

  try {
    // The server rotates the pair and revokes the token just sent, so the new
    // refresh token must be stored too: replaying the old one on the next
    // refresh revokes every session the user has.
    const response = await refreshClient.post('/auth/refresh', { refresh_token: refreshToken });
    const tokens = response.data.data.tokens;
    if (!tokens?.access_token || !tokens?.refresh_token) return null;
    tokenStorage.set(tokens);
    return tokens.access_token;
  } catch {
    return null;
  }
}

function toApiError(error) {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const body = error.response.data;
      return new ApiError(body?.message ?? 'Request failed', {
        status: error.response.status,
        code: body?.code,
        errors: body?.errors ?? [],
        requestId: body?.request_id ?? error.response.headers?.[REQUEST_ID_HEADER.toLowerCase()],
      });
    }
    if (error.code === 'ECONNABORTED') {
      return new ApiError('The request timed out. Please try again.', { status: 0 });
    }
    return new ApiError('Unable to reach the server. Check your connection.', { status: 0 });
  }
  return new ApiError('Unexpected error', { status: 0 });
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401 && error.config) {
      const original = error.config;
      const isRefreshCall = original.url?.includes('/auth/refresh');

      if (!original._retried && !isRefreshCall && tokenStorage.getRefreshToken()) {
        original._retried = true;
        refreshInFlight ??= refreshAccessToken().finally(() => {
          refreshInFlight = null;
        });
        const accessToken = await refreshInFlight;

        if (accessToken) {
          original.headers.set('Authorization', `Bearer ${accessToken}`);
          return apiClient.request(original);
        }
      }

      tokenStorage.clear();
      onSessionExpired();
    }

    return Promise.reject(toApiError(error));
  },
);

/**
 * Turn a signed storage URL into one the browser can actually open.
 *
 * S3 signs an absolute URL and this returns it untouched. Local disk storage
 * signs a path on the API instead, and a bare path would resolve against the
 * SPA's own origin -- which is a different port in development and a
 * different host behind a CDN. Resolving it against the API base is what
 * makes a download work in both places.
 */
export function resolveFileUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const apiOrigin = new URL(config.apiBaseUrl, window.location.origin).origin;
  return new URL(url, apiOrigin).toString();
}

// ---------------------------------------------------------------------------
// Typed helpers -- unwrap the response envelope
// ---------------------------------------------------------------------------
function unwrap(response) {
  return response.data.data;
}

export const api = {
  async get(url, options) {
    return unwrap(await apiClient.get(url, options));
  },

  async getPaginated(url, options) {
    const response = await apiClient.get(url, options);
    return response.data;
  },

  async post(url, body, options) {
    return unwrap(await apiClient.post(url, body, options));
  },

  async put(url, body, options) {
    return unwrap(await apiClient.put(url, body, options));
  },

  async patch(url, body, options) {
    return unwrap(await apiClient.patch(url, body, options));
  },

  async delete(url, options) {
    return unwrap(await apiClient.delete(url, options));
  },

  /** Multipart upload (CVs, documents, profile images). */
  async upload(url, formData, options) {
    return unwrap(
      await apiClient.post(url, formData, {
        ...options,
        headers: { ...options?.headers, 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  /**
   * A file the API generates rather than stores: the report exports.
   *
   * It goes through the client rather than an `<a href>` because the download
   * needs the bearer token, which a plain link cannot carry. That means the
   * bytes arrive in memory and the save has to be arranged by hand -- hence
   * the object URL below, and the revoke that stops it leaking for the life of
   * the tab.
   *
   * The filename comes from `Content-Disposition`, so the server stays the one
   * place that decides what a file is called. Without the header the browser
   * would name it after the last path segment, which for these is `export`
   * with no extension.
   */
  async download(url, options) {
    const response = await apiClient.get(url, { ...options, responseType: 'blob' });
    const disposition = response.headers['content-disposition'] ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    return { blob: response.data, filename: match?.[1] ?? 'download.csv' };
  },
};

/** Hand a blob to the browser as a save. Paired with `api.download`. */
export function saveBlob(blob, filename) {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick rather than immediately: Safari has not finished
  // reading the URL when `click()` returns, and revoking synchronously gives
  // a download that silently produces nothing.
  setTimeout(() => URL.revokeObjectURL(href), 0);
}
